<template>
  <v-dialog
    v-model="dialog"
    max-width="900px"
    scrollable
    persistent
  >
    <v-card v-if="resident" class="medication-profile-card">
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-account-card-details</v-icon>
        Medication Profile
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeDialog">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text v-if="loading" class="text-center pa-8">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <p class="mt-4">Loading resident profile...</p>
      </v-card-text>

      <v-card-text v-else class="pa-6">
        <!-- Patient Info Section (Top) -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card outlined class="pa-4" style="border-color: #c8e6c9 !important;">
              <h3 class="text-h6 mb-3 primary--text">
                <v-icon left color="primary">person</v-icon>
                Patient Information
              </h3>
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <div class="info-item">
                    <span class="info-label">Name:</span>
                    <span class="info-value">{{ resident.fullName || `${resident.firstName} ${resident.lastName}` }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="info-item">
                    <span class="info-label">Date of Birth:</span>
                    <span class="info-value">{{ formatDate(resident.dateOfBirth) }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="info-item">
                    <span class="info-label">Room Number:</span>
                    <span class="info-value">{{ resident.roomNumber || 'N/A' }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <div class="info-item">
                    <span class="info-label">NHS Number:</span>
                    <span class="info-value">{{ resident.nhsNumber || 'N/A' }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Middle Section: Respect Form & Allergies -->
        <v-row class="mb-4">
          <!-- Respect Form Indicator (Left) -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4" style="border-color: #c8e6c9 !important;">
              <h3 class="text-h6 mb-3 primary--text">
                <v-icon left color="primary">favorite</v-icon>
                Respect Form
              </h3>
              <div class="text-center">
                <v-icon size="64" color="primary">mdi-heart</v-icon>
                <p class="mt-2">Respect Form Indicator</p>
              </div>
            </v-card>
          </v-col>

          <!-- Allergies (Right) -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4" style="border-color: #c8e6c9 !important;">
              <h3 class="text-h6 mb-3 primary--text">
                <v-icon left color="primary">warning</v-icon>
                Allergies
              </h3>
              <div v-if="resident.photo" class="text-center mb-3">
                <v-avatar size="120">
                  <img :src="getPhotoUrl(resident.photo)" alt="Resident Photo" />
                </v-avatar>
              </div>
              <div v-else class="text-center mb-3">
                <v-icon size="64" color="grey">person</v-icon>
              </div>
              <p class="text-center">Allergy information to be displayed here</p>
            </v-card>
          </v-col>
        </v-row>

        <!-- Lower Section: Medication Preferences, Medical History, GP Info, NOK Info -->
        <v-row>
          <!-- Medication Preferences -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 mb-4" style="border-color: #c8e6c9 !important;">
              <h3 class="text-h6 mb-3 primary--text">
                <v-icon left color="primary">medication</v-icon>
                Medication Preferences
              </h3>
              <p class="grey--text">Medication preferences information</p>
            </v-card>
          </v-col>

          <!-- Medical History -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4 mb-4" style="border-color: #c8e6c9 !important;">
              <h3 class="text-h6 mb-3 primary--text">
                <v-icon left color="primary">history</v-icon>
                Medical History
              </h3>
              <div v-if="resident.medicalHistoryList && resident.medicalHistoryList.length > 0">
                <ul class="medical-history-list">
                  <li v-for="(item, index) in resident.medicalHistoryList" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
              <div v-else-if="resident.medicalHistory">
                <p>{{ resident.medicalHistory }}</p>
              </div>
              <p v-else class="grey--text">No medical history available</p>
            </v-card>
          </v-col>
        </v-row>

        <!-- GP Information -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="pa-4 mb-4" style="border-color: #c8e6c9 !important;">
              <h3 class="text-h6 mb-3 primary--text">
                <v-icon left color="primary">local_hospital</v-icon>
                General Practitioner (GP) Information
              </h3>
              <v-row v-if="hasGpInfo">
                <v-col cols="12" sm="6" md="4">
                  <div class="info-item">
                    <span class="info-label">Name:</span>
                    <span class="info-value">
                      {{ getGpFullName() }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="info-item">
                    <span class="info-label">Phone:</span>
                    <span class="info-value">{{ resident.gpPhoneNumber || 'N/A' }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                  <div class="info-item">
                    <span class="info-label">Address:</span>
                    <span class="info-value">{{ getGpAddress() }}</span>
                  </div>
                </v-col>
              </v-row>
              <p v-else class="grey--text">No GP information available</p>
            </v-card>
          </v-col>
        </v-row>

        <!-- Next of Kin Information -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="pa-4" style="border-color: #c8e6c9 !important;">
              <h3 class="text-h6 mb-3 primary--text">
                <v-icon left color="primary">contacts</v-icon>
                Next of Kin
              </h3>
              <div v-if="resident.nextOfKins && resident.nextOfKins.length > 0">
                <v-row v-for="(nok, index) in resident.nextOfKins" :key="index" class="mb-3">
                  <v-col cols="12">
                    <v-card flat class="pa-3" style="background-color: #f5f5f5;">
                      <v-row>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <span class="info-label">Name:</span>
                            <span class="info-value">{{ `${nok.firstName} ${nok.lastName}` }}</span>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <span class="info-label">Relationship:</span>
                            <span class="info-value">{{ nok.relationship || 'N/A' }}</span>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <span class="info-label">Phone:</span>
                            <span class="info-value">{{ nok.phoneNumber || nok.landlineNumber || 'N/A' }}</span>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <span class="info-label">Email:</span>
                            <span class="info-value">{{ nok.email || 'N/A' }}</span>
                          </div>
                        </v-col>
                        <v-col cols="12" v-if="nok.address">
                          <div class="info-item">
                            <span class="info-label">Address:</span>
                            <span class="info-value">{{ getNokAddress(nok) }}</span>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
              <p v-else class="grey--text">No Next of Kin information available</p>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="closeDialog">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';

export default {
  name: 'MedicationProfileDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    resident: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    hasGpInfo() {
      return this.resident && (
        this.resident.gpFirstName ||
        this.resident.gpLastName ||
        this.resident.gpAddress ||
        this.resident.gpPhoneNumber
      );
    }
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD-MM-YYYY');
    },
    getPhotoUrl(photo) {
      if (!photo) return '';
      // If photo is already a full URL, return it
      if (photo.startsWith('http://') || photo.startsWith('https://')) {
        return photo;
      }
      // Use the same pattern as ResidentList.vue
      return 'https://ocrb.johnsoncaregroup.com/storage/images/occupancies/' + photo;
    },
    getGpFullName() {
      if (!this.resident) return 'N/A';
      const parts = [];
      if (this.resident.gpFirstName) parts.push(this.resident.gpFirstName);
      if (this.resident.gpLastName) parts.push(this.resident.gpLastName);
      return parts.length > 0 ? parts.join(' ') : 'N/A';
    },
    getGpAddress() {
      if (!this.resident) return 'N/A';
      const parts = [];
      if (this.resident.gpAddress) parts.push(this.resident.gpAddress);
      if (this.resident.gpTown) parts.push(this.resident.gpTown);
      if (this.resident.gpCounty) parts.push(this.resident.gpCounty);
      if (this.resident.gpPostCode) parts.push(this.resident.gpPostCode);
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    },
    getNokAddress(nok) {
      const parts = [];
      if (nok.address) parts.push(nok.address);
      if (nok.town) parts.push(nok.town);
      if (nok.county) parts.push(nok.county);
      if (nok.postCode) parts.push(nok.postCode);
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    }
  }
};
</script>

<style scoped lang="scss">
.medication-profile-card {
  .info-item {
    margin-bottom: 8px;
    
    .info-label {
      font-weight: 600;
      color: #666;
      display: block;
      margin-bottom: 4px;
      font-size: 0.875rem;
    }
    
    .info-value {
      color: #333;
      font-size: 0.9375rem;
      display: block;
    }
  }

  .medical-history-list {
    list-style-type: disc;
    padding-left: 20px;
    margin: 0;
    
    li {
      margin-bottom: 8px;
      color: #333;
    }
  }

  .v-card {
    border-width: 2px !important;
  }
}
</style>

